// frontend/src/pages/HelpPage.jsx
import React, { useState, useCallback } from 'react';
import { useLanguageStore } from '../store/useLanguageStore';
import { useUIStore } from '../store/useUIStore';
import { LanguageSelector } from '../components/LanguageSelector';
import { AppSidebar } from '../components/AppSidebar';
import { Link, useNavigate } from 'react-router-dom';
import {
  Menu, LogOut, HelpCircle, ChevronDown,
  Database, Workflow, FolderKanban, ShieldCheck,
  Users, Mail, Plug, BookOpen,
} from 'lucide-react';
import LogoHorizontal from '../assets/images/datiaFlow_horizontal.jpg';
import Isotipo from '../assets/images/datiaFlow_cuadrado.jpg';

// ── Accordion Section ──────────────────────────────────────────────────

const AccordionItem = ({ title, icon: Icon, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-slate-50/50 transition-colors"
      >
        <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center shrink-0">
          <Icon className="w-4 h-4 text-indigo-600" />
        </div>
        <span className="flex-1 text-sm font-black text-slate-800">{title}</span>
        <ChevronDown
          className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div className="px-5 pb-5 pt-1 space-y-4 border-t border-slate-100">
          {children}
        </div>
      )}
    </div>
  );
};

const HelpEntry = ({ q, a }) => (
  <div className="flex gap-3">
    <div className="w-1 rounded-full bg-indigo-200 shrink-0 mt-1" />
    <div>
      <p className="text-sm font-bold text-slate-700">{q}</p>
      <p className="text-sm text-slate-500 leading-relaxed mt-1 whitespace-pre-line">{a}</p>
    </div>
  </div>
);

// ── Page ────────────────────────────────────────────────────────────────

export const HelpPage = ({ user, setUser }) => {
  const { t, language } = useLanguageStore();
  const { openSidebar } = useUIStore();
  const navigate = useNavigate();

  const handleLogout = useCallback(() => {
    localStorage.removeItem('vnext_auth_token');
    localStorage.removeItem('vnext_user');
    setUser(null);
    navigate('/login');
  }, [setUser, navigate]);

  return (
    <div className="min-h-screen bg-slate-50 font-sans pb-12">
      <AppSidebar user={user} onLogout={handleLogout} />

      {/* ── Header ──────────────────────────────────────────────── */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              onClick={openSidebar}
              aria-label="Abrir menú"
              className="p-2 rounded-xl text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 transition-colors shrink-0"
            >
              <Menu className="w-5 h-5" />
            </button>
            <Link to="/dashboard" className="flex items-center cursor-pointer group shrink-0">
              <img src={Isotipo} alt="DatiaFlow"
                className="block md:hidden h-9 w-9 object-cover object-left rounded-md group-hover:scale-105 transition-transform" />
              <img src={LogoHorizontal} alt="DatiaFlow"
                className="hidden md:block h-11 w-auto group-hover:opacity-90 transition-opacity" />
            </Link>
          </div>
          <div className="flex items-center space-x-6">
            <LanguageSelector />
            <div className="flex items-center space-x-3 border-l border-slate-200 pl-6">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-bold text-slate-700">{user?.name}</p>
              </div>
              <button
                onClick={handleLogout}
                className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition"
                title={t('dashboard.logout') || 'Cerrar sesión'}
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* ── Content ─────────────────────────────────────────────── */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">

        {/* ── Page title ────────────────────────────────────────── */}
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center">
            <HelpCircle className="w-5 h-5" />
          </div>
          <h1 className="text-xl sm:text-2xl font-black text-slate-800">
            {t('help.title') || 'Centro de Ayuda'}
          </h1>
        </div>
        <p className="text-sm text-slate-400 mb-6 ml-[52px]">
          {t('help.subtitle') || 'Todo lo que necesitas saber para usar DatiaFlow.'}
        </p>

        {/* ── Accordion sections ────────────────────────────────── */}
        <div className="space-y-3">

          {/* ═══ 1. Conceptos básicos ═══ */}
          <AccordionItem
            title={t('help.basics.title') || 'Conceptos básicos'}
            icon={BookOpen}
            defaultOpen={true}
          >
            <HelpEntry
              q={t('help.basics.q_pipeline') || '¿Qué es un pipeline?'}
              a={t('help.basics.a_pipeline') || 'Un pipeline es una configuración de migración de datos que define: de dónde leer (origen), a dónde escribir (destino), cómo mapear campos entre ambos, y qué transformaciones aplicar. Una vez configurado, puede ejecutarse manual o automáticamente.'}
            />
            <HelpEntry
              q={t('help.basics.q_syncmode') || '¿Qué diferencia hay entre Agregar, Reemplazar e Integrar?'}
              a={t('help.basics.a_syncmode') || 'Agregar (Append): inserta todos los registros sin verificar si ya existen. Ideal para logs o datos acumulativos.\n\nReemplazar (Overwrite): borra todos los datos del destino y los reescribe desde cero. Útil para tablas de referencia que se actualizan completamente.\n\nIntegrar (Upsert): actualiza los registros que ya existen y crea los nuevos. Es el modo más seguro para migraciones incrementales. Solo disponible para bases de datos (no archivos planos).'}
            />
            <HelpEntry
              q={t('help.basics.q_zdr') || '¿Qué significa "Zero Data Retention"?'}
              a={t('help.basics.a_zdr') || 'DatiaFlow procesa tus datos en memoria RAM de forma temporal y no almacena datos de clientes en el servidor. Los datos fluyen directamente del origen al destino en bloques pequeños (5,000 registros) que se eliminan de la memoria al terminar.\n\nLa única excepción son los archivos exportados (CSV, Excel), que se guardan temporalmente en AWS S3 por un máximo de 7 días para permitir descargas.'}
            />
            <HelpEntry
              q={t('help.basics.q_smartmap') || '¿Cómo funciona el Smart Mapping?'}
              a={t('help.basics.a_smartmap') || 'Smart Mapping usa inteligencia artificial para sugerir automáticamente cómo mapear las columnas del origen con las del destino. Analiza los nombres de los campos y busca coincidencias semánticas (por ejemplo, "nombre_cliente" → "customerName"). Siempre puedes ajustar las sugerencias manualmente.'}
            />
          </AccordionItem>

          {/* ═══ 2. Crear y ejecutar migraciones ═══ */}
          <AccordionItem
            title={t('help.migrations.title') || 'Crear y ejecutar migraciones'}
            icon={Workflow}
          >
            <HelpEntry
              q={t('help.migrations.q_wizard') || '¿Cómo creo un pipeline?'}
              a={t('help.migrations.a_wizard') || 'Desde el Centro de Control (Dashboard), haz clic en "Nuevo pipeline". El asistente te guiará paso a paso:\n\n1. Origen: selecciona el tipo de motor y configura la conexión.\n2. Destino: selecciona dónde quieres escribir los datos.\n3. Mapeo: asigna qué columna del origen va a qué columna del destino.\n4. Revisión: verifica la configuración, haz una prueba (Dry Run) y guarda.'}
            />
            <HelpEntry
              q={t('help.migrations.q_dryrun') || '¿Qué es el Dry Run?'}
              a={t('help.migrations.a_dryrun') || 'Es una simulación que muestra los primeros 50 registros tal como llegarían al destino después de aplicar las transformaciones y el mapeo. No escribe nada en el destino real. Te permite verificar que todo está correcto antes de ejecutar.'}
            />
            <HelpEntry
              q={t('help.migrations.q_transform') || '¿Qué transformaciones puedo aplicar?'}
              a={t('help.migrations.a_transform') || 'DatiaFlow ofrece múltiples transformaciones por campo: conversión de tipos, limpieza de texto (quitar espacios, mayúsculas, minúsculas), condicionales If/Else, valores fijos, operaciones matemáticas, formateo de fechas, extracción de substrings, reemplazos, prefijos/sufijos y hashing. Las reglas se aplican en secuencia.'}
            />
            <HelpEntry
              q={t('help.migrations.q_schedule') || '¿Puedo programar ejecuciones automáticas?'}
              a={t('help.migrations.a_schedule') || 'Sí. En el paso de Revisión puedes configurar la frecuencia: una sola vez, diario, semanal o con expresión CRON personalizada. DatiaFlow maneja automáticamente los cambios de horario de verano usando zonas horarias IANA.'}
            />
            <HelpEntry
              q={t('help.migrations.q_delta') || '¿Cómo migro solo los datos nuevos?'}
              a={t('help.migrations.a_delta') || 'En la sección "¿Qué datos migrar?" del paso de Revisión, selecciona "Solo cambios recientes". DatiaFlow recordará automáticamente hasta dónde llegó la última ejecución y solo procesará los registros nuevos o actualizados. También puedes elegir una fecha específica manualmente.'}
            />
            <HelpEntry
              q={t('help.migrations.q_multitable') || '¿Puedo migrar tablas relacionadas en un solo pipeline?'}
              a={t('help.migrations.a_multitable') || 'Sí, cuando las tablas están relacionadas entre sí. Por ejemplo: origen tiene "clientes" y "telefonos_cliente" separados, pero el destino los necesita en una sola tabla. DatiaFlow te permite hacer JOIN entre las tablas origen para combinar los datos correctamente.\n\nEl modo multi-tabla requiere que definas las relaciones (JOINs en origen, claves foráneas en destino). El sistema respeta automáticamente el orden de integridad referencial.\n\nPara tablas independientes sin relación entre sí, crea un pipeline separado para cada una.'}
            />
          </AccordionItem>

          {/* ═══ 3. Proyectos ERP/CRM ═══ */}
          <AccordionItem
            title={t('help.projects.title') || 'Proyectos de Migración ERP/CRM'}
            icon={FolderKanban}
          >
            <HelpEntry
              q={t('help.projects.q_what') || '¿Qué es un proyecto de migración?'}
              a={t('help.projects.a_what') || 'Un proyecto agrupa múltiples pipelines en fases ordenadas para migrar un sistema completo (ERP, CRM, etc.) de forma orquestada. Ejemplo: Fase 1 → Datos básicos, Fase 2 → Maestros, Fase 3 → Transacciones. Cada fase puede depender de la anterior o ejecutarse en paralelo.'}
            />
            <HelpEntry
              q={t('help.projects.q_rehearsal') || '¿Qué diferencia hay entre Ensayo y Cutover?'}
              a={t('help.projects.a_rehearsal') || 'Ensayo (Rehearsal): ejecución de prueba que no cambia el estado del proyecto. Puedes hacer tantos ensayos como necesites para validar datos, tiempos y rendimiento.\n\nCutover (Día D): es la migración definitiva. Al completarse exitosamente, el proyecto se marca como completado. Solo debe ejecutarse cuando estés seguro de que todo está listo.'}
            />
            <HelpEntry
              q={t('help.projects.q_benchmark') || '¿Cómo veo el rendimiento de cada ejecución?'}
              a={t('help.projects.a_benchmark') || 'Cada ejecución genera un benchmark automático que muestra por pipeline: registros procesados, registros fallidos, MB transferidos, duración y estado. Puedes comparar ensayos para verificar que el rendimiento mejora o se mantiene estable antes del Día D.'}
            />
            <HelpEntry
              q={t('help.projects.q_alert') || '¿Qué es la alerta del Día D?'}
              a={t('help.projects.a_alert') || 'Si programas una fecha de cutover, DatiaFlow envía un email de alerta 24 horas antes a todos los administradores. El email incluye un checklist de preparación para que no se te escape nada.'}
            />
          </AccordionItem>

          {/* ═══ 4. Administración ═══ */}
          <AccordionItem
            title={t('help.admin.title') || 'Administración'}
            icon={Users}
          >
            <HelpEntry
              q={t('help.admin.q_roles') || '¿Qué roles existen?'}
              a={t('help.admin.a_roles') || 'Administrador: puede crear pipelines, gestionar el equipo, crear API keys y webhooks. Tiene acceso completo.\n\nOperador: puede crear y ejecutar pipelines, pero no puede gestionar usuarios ni configuración de la cuenta.\n\nLos roles determinan qué acciones están disponibles en la interfaz.'}
            />
            <HelpEntry
              q={t('help.admin.q_team') || '¿Cómo invito a mi equipo?'}
              a={t('help.admin.a_team') || 'Desde la sección "Equipo" en el menú lateral, haz clic en "Invitar". Ingresa el email del usuario y selecciona su rol. Recibirá un correo con un enlace para establecer su contraseña y acceder a DatiaFlow.'}
            />
            <HelpEntry
              q={t('help.admin.q_apikeys') || '¿Para qué sirven las API Keys?'}
              a={t('help.admin.a_apikeys') || 'Las API Keys permiten que sistemas externos (ERPs, CRMs, scripts CI/CD) ejecuten y consulten tus pipelines de forma automática, sin necesidad de iniciar sesión en DatiaFlow. Cada key tiene permisos configurables y un límite de peticiones por minuto.'}
            />
            <HelpEntry
              q={t('help.admin.q_webhooks') || '¿Cómo recibo notificaciones de ejecución?'}
              a={t('help.admin.a_webhooks') || 'Configura un webhook en la sección "Cliente" para recibir un POST automático cuando un pipeline termine (exitoso o con error). DatiaFlow firma cada envío con HMAC-SHA256 para que puedas verificar la autenticidad. Si tu endpoint falla 10 veces consecutivas, el webhook se pausa automáticamente.'}
            />
            <HelpEntry
              q={t('help.admin.q_plan') || '¿Cómo veo los límites de mi plan?'}
              a={t('help.admin.a_plan') || 'En la sección "Cliente" → pestaña "Plan" puedes ver: cantidad de pipelines, usuarios, MB por ejecución y registros por ejecución que incluye tu suscripción actual. Si necesitas más capacidad, contacta a support@varanext.com.'}
            />
          </AccordionItem>

          {/* ═══ 5. Conectividad y seguridad ═══ */}
          <AccordionItem
            title={t('help.connectivity.title') || 'Conectividad y seguridad'}
            icon={ShieldCheck}
          >
            <HelpEntry
              q={t('help.connectivity.q_engines') || '¿Qué motores de datos soporta DatiaFlow?'}
              a={t('help.connectivity.a_engines') || 'Bases de datos: MySQL, PostgreSQL, Oracle, SQL Server, MongoDB, MariaDB, Azure SQL, BigQuery, Snowflake, Redshift, SAP HANA.\n\nArchivos: CSV, Excel, DBF, Google Sheets.\n\nAplicaciones (SaaS): HubSpot, Salesforce, Dynamics 365, REST API genérica.\n\nTransferencia: FTP, SFTP, AWS S3.\n\nConexión: directa, túnel SSH o whitelist de IP.'}
            />
            <HelpEntry
              q={t('help.connectivity.q_firewall') || '¿Mi base de datos está detrás de un firewall. ¿Cómo conecto?'}
              a={t('help.connectivity.a_firewall') || 'Tienes dos opciones:\n\n1. Whitelist: agrega la IP del servidor de DatiaFlow a las reglas de tu firewall. La IP se muestra al configurar la conexión.\n\n2. Túnel SSH: DatiaFlow se conecta a un servidor bastión que tú controlas, y desde ahí accede a tu base de datos. Solo necesitas un usuario SSH con acceso.'}
            />
            <HelpEntry
              q={t('help.connectivity.q_encryption') || '¿Mis credenciales están seguras?'}
              a={t('help.connectivity.a_encryption') || 'Sí. Todas las credenciales se cifran con Fernet (AES-128) en el momento de guardarlas y solo se descifran justo cuando se necesitan para conectar. Nunca se almacenan en texto plano ni aparecen en logs. La conexión entre tu navegador y DatiaFlow está protegida con HTTPS.'}
            />
            <HelpEntry
              q={t('help.connectivity.q_ssh') || '¿Cómo configuro un túnel SSH?'}
              a={t('help.connectivity.a_ssh') || 'Al configurar una conexión de base de datos, activa la opción "Túnel SSH" y proporciona: host del bastión, puerto SSH (normalmente 22), usuario SSH y contraseña o clave privada. DatiaFlow creará un túnel seguro para acceder a tu base de datos sin exponer puertos al internet.'}
            />
          </AccordionItem>

          {/* ═══ 6. Contacto ═══ */}
          <AccordionItem
            title={t('help.contact.title') || 'Contacto y soporte'}
            icon={Mail}
          >
            <HelpEntry
              q={t('help.contact.q_support') || '¿Cómo contacto al soporte?'}
              a={t('help.contact.a_support') || 'Envía un correo a support@varanext.com con la descripción de tu problema o consulta. Nuestro equipo responderá lo antes posible. Incluye capturas de pantalla y el nombre del pipeline si es un error técnico.'}
            />
            <HelpEntry
              q={t('help.contact.q_docs') || '¿Hay documentación de la API?'}
              a={t('help.contact.a_docs') || 'Sí. Si tienes una API Key activa, puedes acceder a la documentación interactiva (Swagger) desde la sección "Cliente" → pestaña "Integración". Ahí encontrarás ejemplos de código, referencia de endpoints y guías de verificación de webhooks.'}
            />
          </AccordionItem>
        </div>

        {/* ── Footer help ───────────────────────────────────────── */}
        <div className="mt-8 text-center">
          <p className="text-xs text-slate-400">
            {t('help.footer') || '¿No encuentras lo que buscas?'}{' '}
            <a href="mailto:support@varanext.com" className="text-indigo-600 hover:underline font-bold">
              support@varanext.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default HelpPage;
